/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox 白板の「実行時モデル ↔ 保存モデル」変換。実行時は描画都合の相対添字（メンバー行の index / 日付の index / 週の index）で位置を持つが、保存は安定識別子（ニックネーム + ISO 日付）で持つため、読み込み時に現在のメンバー一覧・表示期間へ解決し直し、保存時に絶対値へ戻す。現在の表示期間に解決できないブロックは「見えないが保存は保つ」形で持ち越し、全置換保存で消えないようにする。配置を持たないブロック（共有トレイ導入前の保存・担当者が名簿から消えた予定）はチーム白板の持ち物にせず、共有トレイへ引き取るぶんとして分けて返す
 * @graph-connects adoptTrayBlock [calls] 未配置ブロックの共有トレイ住人への正規化
 */
import type {
  PjSandboxStoredBlock,
  PjSandboxStoredCandidate,
} from '@cortex/domain-product-innovation-lab-types';
import type { PoolWeek } from './constants.js';
import { adoptTrayBlock } from './sharedTray.js';
import type { CandidateCase, SandboxBlock, SandboxDay, SandboxMember } from './types.js';

/**
 * 保存モデルから実行時モデルへ解決した結果。
 *
 * `carryOver` は「保存には残すが、いまは白板に出さない」ブロック。表示期間（過去1週〜未来4週）に
 * 1 日も掛からない配置（終了日が期間より前 / 開始日が期間より先）がこれにあたる。保存は全置換
 * なので、これを持ち回さずに保存すると **画面に出ていないだけの過去の予定が消える**。実行時の state とは別に保持し、保存時に
 * 書き戻すことで、白板を開いた日によって履歴が削れることを防ぐ。
 */
export interface ResolvedBoardState {
  /** 白板に描画する配置済みブロック。 */
  blocks: SandboxBlock[];
  /**
   * この白板の文書に未配置のまま入っていたため、共有トレイへ引き取るブロック。
   * 呼び出し側が共有トレイ側の state へ移し、白板の文書からは落とす。
   */
  adoptedTrayBlocks: SandboxBlock[];
  /** 候補案件プールに並ぶ候補。 */
  candidates: CandidateCase[];
  /** 表示期間外のため描画しないが、保存では保持し続けるブロック。 */
  carryOver: PjSandboxStoredBlock[];
}

/** メンバーのニックネーム（小文字）を返す。未設定なら表示名で代替する。 @graph-connects none */
function memberKey(member: SandboxMember): string {
  return (member.nickName ?? member.name).toLowerCase();
}

/**
 * ニックネーム（小文字）→ メンバー行インデックスの索引を作る。
 * @graph-connects none
 */
function buildMemberIndex(members: SandboxMember[]): Map<string, number> {
  const index = new Map<string, number>();
  members.forEach((m, i) => {
    index.set(memberKey(m), i);
  });
  return index;
}

/** 1 日のミリ秒。ISO 日付同士の差を日数へ直すのに使う。 @graph-connects none */
const MS_PER_DAY = 86_400_000;

/**
 * ISO 日付 (YYYY-MM-DD) を UTC の epoch ms にする。日付同士の差だけに使うので、
 * 1 日が 23/25 時間になりうるローカル時刻ではなく UTC 固定で解釈する。
 * @graph-connects none
 */
function isoToUtcMs(iso: string): number | null {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (matched === null) return null;
  const [, year = '', month = '', date = ''] = matched;
  return Date.UTC(Number(year), Number(month) - 1, Number(date));
}

/**
 * 表示期間の先頭 (`days[0]`) から数えた日オフセットへ直す。
 *
 * 日付 → 列の索引引きにしないのは、**表示期間の外にはみ出した配置も座標として表せるように**
 * するため。表示期間は毎週月曜に 1 週ぶん先へ動くので、索引引きだと「先週より前に始まった
 * だけで、まだ終わっていない予定」が解決できなくなり、白板から消えてしまう。期間内なら
 * `days[n].index` と同じ値を返し、期間より前なら負値、先なら `days.length` 以上を返す。
 * @graph-connects none
 */
function dayOffsetOf(days: SandboxDay[], iso: string): number | null {
  const base = days[0];
  if (base === undefined) return null;
  const from = isoToUtcMs(base.iso);
  const to = isoToUtcMs(iso);
  if (from === null || to === null) return null;
  return Math.round((to - from) / MS_PER_DAY);
}

/**
 * 日オフセットを ISO 日付へ戻す（`dayOffsetOf` の逆変換）。表示期間の外を指す
 * オフセット（負値 / `days.length` 以上）も日付へ戻せるため、期間からはみ出した
 * 配置を保存しても開始日が失われない。
 * @graph-connects none
 */
function isoAtDayOffset(days: SandboxDay[], offset: number): string | null {
  const base = days[0];
  if (base === undefined) return null;
  const from = isoToUtcMs(base.iso);
  if (from === null) return null;
  const d = new Date(from + offset * MS_PER_DAY);
  const year = d.getUTCFullYear().toString();
  const month = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const date = d.getUTCDate().toString().padStart(2, '0');
  return `${year}-${month}-${date}`;
}

/**
 * 保存済みの白板状態を、現在のメンバー一覧・表示期間・週セクションに合わせて解決する。
 *
 * 解決規則（いずれも「黙って別の場所に置く」ことを避けるための倒し方）:
 * - 未配置で保存されている（共有トレイを分ける前の保存）→ **共有トレイへ引き取る**
 *   (`adoptedTrayBlocks`)。白板の持ち物のままにすると、未配置なのにチームタブを
 *   切り替えた瞬間に画面から消える。
 * - メンバーが見つからない（退職・異動でメンバー行から消えた）→ 同じく **共有トレイへ**。
 *   行インデックスをそのまま使うと別人の行に置かれてしまうため、担当者不明として
 *   全チームから拾い直せる場所に出す。
 * - 表示期間に 1 日も掛からない（終了日が期間より前 / 開始日が期間より先）→ **carryOver** へ。
 *   トレイに出すと毎朝トレイが過去の予定で溢れるうえ、保存し直すと配置情報そのものを失うので、
 *   描画せず保存だけ保つ。判定に使うのは **終了日** で、開始日ではない。表示期間は毎週月曜に
 *   1 週ぶん先へ動くため、開始日で切ると「先週より前に始まっただけで、まだ続いている予定」が
 *   週明けに白板から消え、その案件の候補も「配置済み」を失って未着手へ戻ってしまう。
 *   期間より前に始まったぶんは開始日を負のオフセットのまま持ち、描画側で左端を切り取る。
 * - 候補案件の週が表示範囲より過去 → 当週（week 0）へ寄せる。まだ着手していない候補は
 *   消さずに手前へ繰り上げるのが運用上の期待。範囲より先なら最終週へ丸める。
 *
 * @graph-connects none
 */
export function resolveStoredBoard(input: {
  blocks: PjSandboxStoredBlock[];
  candidates: PjSandboxStoredCandidate[];
  members: SandboxMember[];
  days: SandboxDay[];
  weeks: PoolWeek[];
  teamKey: string;
}): ResolvedBoardState {
  const memberIndex = buildMemberIndex(input.members);

  const blocks: SandboxBlock[] = [];
  const adoptedTrayBlocks: SandboxBlock[] = [];
  const carryOver: PjSandboxStoredBlock[] = [];

  for (const stored of input.blocks) {
    const base: SandboxBlock = {
      id: stored.id,
      name: stored.name,
      sizePt: stored.sizePt,
      kind: stored.kind,
      teamKey: input.teamKey,
      placement: null,
      ...(stored.sourceCandidateId === undefined
        ? {}
        : { sourceCandidateId: stored.sourceCandidateId }),
      ...(stored.issueKey === undefined ? {} : { issueKey: stored.issueKey }),
    };

    const placement = stored.placement;
    if (placement === null) {
      adoptedTrayBlocks.push(adoptTrayBlock(base, input.teamKey));
      continue;
    }

    const startDay = dayOffsetOf(input.days, placement.startDate);
    if (startDay === null || startDay >= input.days.length || startDay + placement.spanDays <= 0) {
      // 表示期間に 1 日も掛からない（終わった予定 / 期間より先の予定）。
      // 描画はしないが保存では持ち続ける。
      carryOver.push(stored);
      continue;
    }

    const rowIndex = memberIndex.get(placement.memberNickName.toLowerCase());
    if (rowIndex === undefined) {
      // 担当メンバーが白板から消えた。誤った行に置かず、拾い直せる共有トレイへ。
      adoptedTrayBlocks.push(adoptTrayBlock(base, input.teamKey));
      continue;
    }

    blocks.push({
      ...base,
      placement: { rowIndex, startDay, spanDays: placement.spanDays },
    });
  }

  const lastWeekIndex = Math.max(0, input.weeks.length - 1);
  const weekIndexByDate = new Map(input.weeks.map((w) => [w.startDate, w.index]));
  const firstWeekDate = input.weeks[0]?.startDate ?? '';

  const candidates: CandidateCase[] = input.candidates.map((stored) => {
    const exact = weekIndexByDate.get(stored.weekStartDate);
    const weekIndex = exact ?? (stored.weekStartDate < firstWeekDate ? 0 : lastWeekIndex);
    return {
      id: stored.id,
      title: stored.title,
      sizePt: stored.sizePt,
      kind: stored.kind,
      teamKey: input.teamKey,
      weekIndex,
      ...(stored.issueKey === undefined ? {} : { issueKey: stored.issueKey }),
    };
  });

  return { blocks, adoptedTrayBlocks, candidates, carryOver };
}

/**
 * 実行時のブロックを保存モデルへ変換する。`carryOver`（表示期間外で描画していないブロック）は
 * そのまま末尾に足し、全置換保存で失われないようにする。
 *
 * 行インデックスが現在のメンバー一覧の範囲外を指している（＝担当者を解決できない）配置は、
 * 誤った担当者で保存しないよう未配置として書き出す。日は表示期間の外でも日付へ戻せる
 * （`isoAtDayOffset`）ので、期間からはみ出した配置の開始日は保存で失われない。
 *
 * @graph-connects none
 */
export function toStoredBlocks(input: {
  blocks: SandboxBlock[];
  members: SandboxMember[];
  days: SandboxDay[];
  carryOver: PjSandboxStoredBlock[];
}): PjSandboxStoredBlock[] {
  const stored: PjSandboxStoredBlock[] = input.blocks.map((block) => {
    const placement = block.placement;
    const member = placement === null ? undefined : input.members[placement.rowIndex];
    const startDate = placement === null ? null : isoAtDayOffset(input.days, placement.startDay);

    return {
      id: block.id,
      name: block.name,
      sizePt: block.sizePt,
      kind: block.kind,
      placement:
        placement === null || member === undefined || startDate === null
          ? null
          : {
              memberNickName: memberKey(member),
              startDate,
              spanDays: placement.spanDays,
            },
      ...(block.sourceCandidateId === undefined
        ? {}
        : { sourceCandidateId: block.sourceCandidateId }),
      ...(block.issueKey === undefined ? {} : { issueKey: block.issueKey }),
    };
  });

  return [...stored, ...input.carryOver];
}

/**
 * 実行時の候補案件を保存モデルへ変換する。週は相対添字ではなく、その週の月曜の
 * ISO 日付で保存する。週セクションに解決できない添字は当週へ寄せる。
 * @graph-connects none
 */
export function toStoredCandidates(
  candidates: CandidateCase[],
  weeks: PoolWeek[],
): PjSandboxStoredCandidate[] {
  const fallback = weeks[0]?.startDate ?? '';
  return candidates.map((candidate) => ({
    id: candidate.id,
    title: candidate.title,
    sizePt: candidate.sizePt,
    kind: candidate.kind,
    weekStartDate: weeks[candidate.weekIndex]?.startDate ?? fallback,
    ...(candidate.issueKey === undefined ? {} : { issueKey: candidate.issueKey }),
  }));
}

/**
 * 編集履歴の `before` / `after` に載せる配置トークンを組み立てる。
 * `"ray 8/31"` のように「担当者 + 開始日」を locale 非依存の短い文字列で表す
 * （表示側の i18n テンプレートがこれをそのまま埋め込む）。解決できない場合は null。
 * @graph-connects none
 */
export function placementToken(
  placement: { rowIndex: number; startDay: number } | null,
  members: SandboxMember[],
  days: SandboxDay[],
): string | null {
  if (placement === null) return null;
  const member = members[placement.rowIndex];
  const iso = isoAtDayOffset(days, placement.startDay);
  if (member === undefined || iso === null) return null;
  const [, month = '', date = ''] = iso.split('-');
  return `${member.name} ${Number(month).toString()}/${Number(date).toString()}`;
}
